import {
  IKeyPath,
  IKeyPathElement,
  IKeyPathFieldElement,
  IKeyPathModelElement,
  IKeyPathTranslationElement,
  IKeyPathViewElement,
  IKeyPathChatElement,
  KeyPathType,
  IKeyPathNotesElement,
  IKeyPathPageElement,
} from '@engoo/realtime-protocol/keypath';
import { EntityFields, EntityStringId, IEntity, IEntityType } from 'app/types/entity';
import { List, Record } from 'immutable';
import React, { Component, ReactNode, useContext, useMemo, FunctionComponent } from 'react';
import { defaultMemoize } from 'reselect';
import { ENGLISH, Language } from 'app/constants/enums/Language';
import { makeStreamPosition, StreamPosition } from '@engoo/realtime-protocol/persisted';
import { makePageNumber, PageNumber } from '@engoo/realtime-protocol/util/pageNumber';

type ConstructorArgs<T extends IKeyPathElement> = Pick<T, Exclude<keyof T, 'type'>>;

// Structs used as map keys
export class KeyPathModelElement extends Record<IKeyPathModelElement>({
  type:  KeyPathType.Model,
  model: '',
  id:    '',
}, 'KeyPathModelElement') {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: ConstructorArgs<IKeyPathModelElement>) {
    super(props);
  }
}

export class KeyPathFieldElement extends Record<IKeyPathFieldElement>({
  type: KeyPathType.Field,
  model: '',
  id: '',
  field: '',
}, 'KeyPathFieldElement') {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: ConstructorArgs<IKeyPathFieldElement>) {
    super(props);
  }
}

export class KeyPathTranslationElement extends Record<IKeyPathTranslationElement>({
  type: KeyPathType.Translation,
  model: '',
  id: '',
  field: '',
  language: ENGLISH.id,
}) {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: ConstructorArgs<IKeyPathTranslationElement>) {
    super(props);
  }
}

export class KeyPathViewElement extends Record<IKeyPathViewElement>({
  type:     KeyPathType.View,
  viewName: '',
}, 'KeyPathViewElement') {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: ConstructorArgs<IKeyPathViewElement>) {
    super(props);
  }
}

export class KeyPathChatElement extends Record<IKeyPathChatElement>({
  type: KeyPathType.Chat,
  position: makeStreamPosition(0),
}, 'KeyPathChatElement') {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: ConstructorArgs<IKeyPathChatElement>) {
    super(props);
  }
}

export class KeyPathNotesElement extends Record<IKeyPathNotesElement>({
  type: KeyPathType.Notes,
}, 'KeyPathNotesElement') {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: ConstructorArgs<IKeyPathNotesElement>) {
    super(props);
  }
}

export class KeyPathPageElement extends Record<IKeyPathPageElement>({
  type: KeyPathType.Page,
  page: makePageNumber(0),
}, 'KeyPathPageElement') {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: ConstructorArgs<IKeyPathPageElement>) {
    super(props);
  }
}

export type KeyPathElement =
  KeyPathModelElement |
  KeyPathFieldElement |
  KeyPathViewElement |
  KeyPathTranslationElement |
  KeyPathChatElement |
  KeyPathNotesElement |
  KeyPathPageElement;

export type KeyPath = List<KeyPathElement>;

const Context = React.createContext<KeyPath>(List());

interface IAddSyncKeyPathProps {
  /**
   * Either a single key or a keypath to be appended to the value
   * from context
   */
  value: KeyPath | KeyPathElement;
  /**
   * If `children` is a function it's gonna pass in the path
   * directly. If it's a react element, it's going to expose the
   * path through context
   */
  children: ReactNode;
}

const useConcatKeyPath = (value: KeyPath | KeyPathElement) => {
  const base = useContext(Context);
  const concat = useMemo(
    () => List.isList(value) ? base.concat(value) : base.push(value)
  , [base, value]);

  return concat;
};

export const useNotesKeyPath = () => {
  const notesPath = useMemo(() => new KeyPathNotesElement({ type: KeyPathType.Notes }), []);
  return useConcatKeyPath(notesPath);
};

export const AddSyncKeyPath: FunctionComponent<IAddSyncKeyPathProps> = ({ value, children }) => {
  const newPath = useConcatKeyPath(value);
  return (
    <Context.Provider value={newPath}>
      {children}
    </Context.Provider>
  );
};

export const useSyncKeyPath = ({ value }: Omit<IAddSyncKeyPathProps, 'children'>) => {
  return useConcatKeyPath(value);
};

/**
 * Resets the keypath for its children. Useful if embedded view
 * needs to have independent paths
 */
export const SyncRoot = ({ children }: { children: ReactNode }) => (
  <Context.Provider value={List()}>
    {children}
  </Context.Provider>
);

/**
 * Adds a model key path to its children.
 */
export class AddModelKeyPath<T extends IEntity> extends Component<{
  type: IEntityType<T>,
  id: EntityStringId,
  children: IAddSyncKeyPathProps['children'],
}> {
  private readonly getPathElement = defaultMemoize(
    (type: IEntityType<T>, id: EntityStringId) => new KeyPathModelElement({
      model: type.typeName,
      id,
    }),
  );

  public render() {
    const { type, id, children } = this.props;
    return <AddSyncKeyPath value={this.getPathElement(type, id)} children={children} />;
  }
}

export const useModelKeyPath = <T extends IEntity>({ id, type }: { type: IEntityType<T>, id: EntityStringId }) => {
  const modelKeyPath = useMemo(() => (
    new KeyPathModelElement({ model: type.typeName, id })
  ), [id, type]);
  return useConcatKeyPath(modelKeyPath);
};


export class AddFieldKeyPath<T extends IEntity> extends Component<{
  type: IEntityType<T>,
  id: EntityStringId,
  field: keyof EntityFields<T>,
  children: IAddSyncKeyPathProps['children'],
}> {
  private readonly getPathElement = defaultMemoize(
    (type: IEntityType<T>, id: EntityStringId, field: keyof EntityFields<T>) => new KeyPathFieldElement({
      model: type.typeName,
      id,
      field,
    }),
  );

  public render() {
    const { type, id, field, children } = this.props;
    return <AddSyncKeyPath value={this.getPathElement(type, id, field)} children={children} />;
  }
}

export const useFieldKeyPath = <T extends IEntity>({
  id,
  type,
  field,
}: { type: IEntityType<T>, id: EntityStringId, field: keyof EntityFields<T> }) => {
  const fieldKeyPath = useMemo(() => (
    new KeyPathFieldElement({ model: type.typeName, id, field })
  ), [id, type, field]);
  return useConcatKeyPath(fieldKeyPath);
};

export class AddTranslationKeyPath<T extends IEntity> extends Component<{
  type: IEntityType<T>,
  id: EntityStringId,
  field: keyof EntityFields<T>,
  language: Language,
  children: IAddSyncKeyPathProps['children'],
}> {
  private readonly getPathElement = defaultMemoize(
    (type: IEntityType<T>, id: EntityStringId, field: keyof EntityFields<T>, language: Language) =>
      new KeyPathTranslationElement({
        model: type.typeName,
        id,
        field,
        language: language.id,
      }),
  );

  public render() {
    const { type, id, field, language, children } = this.props;
    return <AddSyncKeyPath value={this.getPathElement(type, id, field, language)} children={children} />;
  }
}

export const useTranslationKeyPath = <T extends IEntity>({
  id,
  type,
  field,
  language,
}: { type: IEntityType<T>, id: EntityStringId, field: keyof EntityFields<T>, language: Language }) => {
  const translationKeyPath = useMemo(() => (
     new KeyPathTranslationElement({
       model: type.typeName,
       id,
       field,
       language: language.id,
     })
  ), [id, type, field, language]);
  return useConcatKeyPath(translationKeyPath);
};

export class AddViewKeyPath extends Component<{
  viewName: string,
  children: IAddSyncKeyPathProps['children'],
}> {
  private readonly getPathElement = defaultMemoize(
    (viewName: string) => new KeyPathViewElement({ viewName }),
  );

  public render() {
    const { viewName, children } = this.props;
    return <AddSyncKeyPath value={this.getPathElement(viewName)} children={children} />;
  }
}

export const useViewKeyPath = ({ viewName }: { viewName: string }) => {
  const viewKeyPath = useMemo(() => (
    new KeyPathViewElement({ viewName })
  ), [viewName]);
  return useConcatKeyPath(viewKeyPath);
};

export class AddChatKeyPath extends Component<{
  position: StreamPosition,
  children: IAddSyncKeyPathProps['children'],
}> {
  private readonly getPathElement = defaultMemoize(
    (position: StreamPosition) => new KeyPathChatElement({ position }),
  );

  public render() {
    const { position, children } = this.props;
    return <AddSyncKeyPath value={this.getPathElement(position)} children={children} />;
  }
}

export const useChatKeyPath = ({ position }: { position: StreamPosition }) => {
  const chatKeyPath = useMemo(() => (
    new KeyPathChatElement({ position })
  ), [position]);

  return useConcatKeyPath(chatKeyPath);
};

export class AddNotesKeyPath extends Component<{
  children: IAddSyncKeyPathProps['children'],
}> {
  private readonly getPathElement = defaultMemoize(
    () => new KeyPathNotesElement({ type: KeyPathType.Notes }),
  );

  public render() {
    const { children } = this.props;
    return <AddSyncKeyPath value={this.getPathElement()} children={children} />;
  }
}

export const usePageKeyPath = ({ page }: { page: PageNumber }) => {
  const pageKeyPath = useMemo(() => (
    new KeyPathPageElement({ page })
  ), [page]);

  return useConcatKeyPath(pageKeyPath);
};

export const GetKeyPath = Context.Consumer;

const parseKeyPathElement = (x: IKeyPathElement): KeyPathElement => {
  switch (x.type) {
    case KeyPathType.Field:
      return new KeyPathFieldElement(x);
    case KeyPathType.Model:
      return new KeyPathModelElement(x);
    case KeyPathType.Translation:
      return new KeyPathTranslationElement(x);
    case KeyPathType.View:
      return new KeyPathViewElement(x);
    case KeyPathType.Chat:
      return new KeyPathChatElement(x);
    case KeyPathType.Notes:
      return new KeyPathNotesElement(x);
    case KeyPathType.Page:
      return new KeyPathPageElement(x);
  }
};

export const parseStateKey = (elements: IKeyPath): KeyPath => {
  return List(elements).map(parseKeyPathElement);
};
